import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import invariant from 'helpers/invariant';
import classNames from 'classnames';
import {
    NSBadge,
    NSButton,
    NSDropdown,
    NSInput,
    NSSelect,
    NSModal,
} from 'bricks';
import getCRMIntegrationStatusQueryOptions from 'views/DealDashboard/Activity/hooks/useGetCRMIntegrations';
import { useQuery } from '@tanstack/react-query';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { SortingState } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { generatePath, Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from 'contexts/AuthContext';
import RenderIf from 'components/RenderIf/RenderIf';
import { URLS } from 'services/urls';
import { camelToSnakeCase } from 'ns_libs/formatter';
import { INSInfoBannerProps } from 'components/NSInfoBanner/NSInfoBanner';
import { ReportType, ReportTypeToUrlParamMap } from 'views/Reports/types';
import BannerCarousel from 'components/BannerCarousel/BannerCarousel';
import { SignUpStage } from 'views/public/SignUp/SignUpContext';
import { clearLocalStorage, getLocalStorage } from 'helpers/localStorage/helpers';
import NavMenu, { DEALS_SECTIONS } from './NavMenu/NavMenu';
import DealsTable from './DealsTable/DealsTable';
import VisibleColumnsDropdown from './VisibleColumnsDropdown/VisibleColumnsDropdown';
import './Deals.scss';
import {
    COLUMN_OPTIONS,
    DEAL_PIPELINE_SELECTED_STAGES,
    DEALS_ACTIVE_SELECTION,
    DEALS_TABLE_UNIT_TYPE,
    getInitialColumnVisibility,
    IS_EXPANDED_PROJECT_PIPELINE,
    UNIT_TYPES,
    UnitType,
    ACTIONS_COLUMN_ID, SELECT_COLUMN_ID,
    TABLE_KEYS,
    DISABLED_PRO_FORMA_BTN_TOOLTIP,
} from './constants';
import { Deal, IVisibleColumns } from './types';
import { DealsOverviewProvider } from './context/DealsOverviewProvider';
import PipelineSidebar from './PipelineSidebar/PipelineSidebar';
import MapView from './MapView/MapView';
import { useGetDeals } from './hooks/useFetchDeals';
import useDebounce from '../../hooks/useDebounce';
import { useGetDealGroups } from './hooks/useGetDealGroups';
import DealGroupMenu from './DealsTable/BulkActions/DealGroupMenu';
import DeleteDealGroupModal from './DealsTable/BulkActions/DeleteDealGroupModal';
import RenameDealGroupModal from './DealsTable/BulkActions/RenameDealGroupModal';
import { PageSpecificKeys, useLocalStorage } from '../../helpers/localStorage';
import { getSubprojectStageIds } from './helpers/subprojectStages';
import EmptyState from './EmptyState/EmptyState';
import { useGetShowCreateCombinedReport } from './hooks/useGetShowCreateCombinedReport';
import { useGetSubprojectsStages } from './hooks/useGetSubprojectStages';
import useGetDealsWithProFormas from '../ProFormaTable/hooks/useGetDealsWithProFormas';
import CreateProFormaModal from '../CreateProFormaModal/CreateProFormaModal';
import { IOptionAnyValue } from '../../bricks/types';

const Deals = () => {
    const { selectedOrganizationId } = useAuthContext();
    invariant(selectedOrganizationId, 'Selected organization ID is required');

    const locallySavedActiveSection = window.localStorage.getItem(DEALS_ACTIVE_SELECTION);
    const [activeSection, setActiveSection] = useState<string | null>(locallySavedActiveSection || DEALS_SECTIONS.MAP);
    const [searchValue, setSearchValue] = useState<string>('');
    const [showDealSelectionModal, setShowDealSelectionModal] = useState(false);
    const [selectedDealIdForProForma, setSelectedDealIdForProForma] = useState<number | null>(null);
    const [openCreateProFormaModal, setOpenCreateProFormaModal] = useState(false);
    const [selectedDealOption, setSelectedDealOption] = useState<IOptionAnyValue | null>(null);

    const signUpStage = getLocalStorage<SignUpStage>(PageSpecificKeys.SignUpStage);
    const [locallyStoredVisibleColumns, setLocallyStoredVisibleColumns] = useLocalStorage(PageSpecificKeys.DealsPage, getInitialColumnVisibility());
    const [locallyStoredColumnOrder, setLocallyStoredColumnOrder] = useLocalStorage<string[]>(
        PageSpecificKeys.DealsPageColumnOrder,
        COLUMN_OPTIONS.map(col => col.value).filter(
            key => key !== SELECT_COLUMN_ID && key !== ACTIONS_COLUMN_ID,
        ), // Default column order without first and last columns
    );
    const [showBanners, setShowBanners] = useLocalStorage<Record<string, boolean>>(PageSpecificKeys.DealPageBanners, {});

    const [visibleColumns, setVisibleColumns] = useState<IVisibleColumns>(() => ({
        ...getInitialColumnVisibility(),
        ...locallyStoredVisibleColumns,
    }));

    const [columnOrder, setColumnOrder] = useState<string[]>(() => [SELECT_COLUMN_ID, ...locallyStoredColumnOrder, ACTIONS_COLUMN_ID]);
    const [selectedUnitType, setSelectedUnitType] = useState<UnitType>(() => {
        const storedValue = window.localStorage.getItem(DEALS_TABLE_UNIT_TYPE);
        return storedValue ? JSON.parse(storedValue) : UNIT_TYPES.GROSS_BUILD_SF;
    });
    const [isExpandedPipeline, setIsExpandedPipeline] = useState<boolean>(() => {
        const storedValue = window.localStorage.getItem(IS_EXPANDED_PROJECT_PIPELINE);
        return storedValue ? JSON.parse(storedValue) : false;
    });
    const [selectedStages, setSelectedStages] = useState<Record<string, boolean>>(() => {
        const locallySavedSelectedStages = window.localStorage.getItem(DEAL_PIPELINE_SELECTED_STAGES);
        return locallySavedSelectedStages ? JSON.parse(locallySavedSelectedStages) : {};
    });
    const [selectedDealGroup, setSelectedDealGroup] = useState<number | undefined>(undefined);
    const [selectedDeal, setSelectedDeal] = useState<Deal>({} as Deal);
    const [isOpenDeleteDealGroupModal, setIsOpenDeleteDealGroupModal] = useState(false);
    const [isOpenRenameDealGroupModal, setIsOpenRenameDealGroupModal] = useState(false);
    const [storedSorting, setStoredSorting] = useLocalStorage<SortingState>(PageSpecificKeys.DealsPageSorting, []);

    const [sortBy, setSortBy] = useState<string>(() => {
        if (storedSorting && storedSorting.length > 0) {
            return camelToSnakeCase(storedSorting[0].id);
        }
        return camelToSnakeCase(TABLE_KEYS.dateCreated);
    });

    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
        if (storedSorting && storedSorting.length > 0) {
            return storedSorting[0].desc ? 'desc' : 'asc';
        }
        return 'desc';
    });

    const defaultSorting: SortingState = [
        {
            id: TABLE_KEYS.dateCreated,
            desc: true,
        },
    ];

    const [sorting, setSorting] = useState<SortingState>(storedSorting.length > 0 ? storedSorting : defaultSorting);

    const [isOpenEmailIntegrationModal, setIsOpenEmailIntegrationModal] = useState(false);

    const debouncedValue = useDebounce(searchValue, 500);
    const navigate = useNavigate();
    const { data: subprojectStages } = useGetSubprojectsStages();

    const handleDealSelection = useCallback((deal: Deal) => {
        setSelectedDeal(deal);
    }, []);

    const handleSelectUnitType = (unitType: UnitType) => {
        setSelectedUnitType(unitType);
        window.localStorage.setItem(DEALS_TABLE_UNIT_TYPE, JSON.stringify(unitType));
    };

    // Pagination state
    const [pageSize, setPageSize] = useState(() => Number(localStorage.getItem('pageSize')) || 10);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        localStorage.setItem('pageSize', pageSize.toString());
    }, [pageSize]);

    const isOptionDisplayed = (key: string) => true; // Adjust logic

    const derivedSelectedStages = useMemo(() => {
        if (Object.keys(selectedStages).length > 0) {
            return selectedStages;
        }
        if (subprojectStages) {
            return subprojectStages.reduce((acc, stage) => ({
                ...acc,
                [stage.correlationId]: true,
            }), {});
        }
        return {};
    }, [selectedStages, subprojectStages]);

    const selectedSubprojectStageIds = getSubprojectStageIds(derivedSelectedStages);

    // Fetch deals using the custom hook
    const { data: dealGroups, isLoading: isLoadingDealGroups } = useGetDealGroups(selectedOrganizationId);

    const { data: deals, isLoading: dealsAreLoading } = useGetDeals({
        pageSize,
        currentPage,
        orgId: selectedOrganizationId,
        searchValue: debouncedValue,
        sortBy,
        sortOrder,
        groupId: selectedDealGroup,
        subprojectStageIds: getSubprojectStageIds(derivedSelectedStages),
        selectedUnitType: sortBy === 'units' ? selectedUnitType : undefined,
    });
    const { data: showCreateCombinedReport } = useGetShowCreateCombinedReport({
        orgId: selectedOrganizationId,
    });

    const { data: dealsWithProFormas } = useGetDealsWithProFormas({
        orgId: selectedOrganizationId!,
        enabled: !!selectedOrganizationId,
        includeAllDealsWithProFormas: true,
    });

    useEffect(() => {
        if (deals?.items?.[0]) {
            setSelectedDeal(deals.items[0]);
        }
    }, [deals]);

    const handleSort = (columnName: string, order: 'asc' | 'desc') => {
        setSortBy(camelToSnakeCase(columnName));
        setSortOrder(order);
    };

    const handleAscendingSort = (columnName: string) => {
        handleSort(columnName, 'asc');
    };

    const handleDescendingSort = (columnName: string) => {
        handleSort(columnName, 'desc');
    };

    const noDealsAcrossAllStages = deals?.unfilteredCount === 0;
    const isEmpty = deals?.items.length === 0;

    const updateVisibleColumns = (columns: IVisibleColumns) => {
        setVisibleColumns(columns);
        setLocallyStoredVisibleColumns(columns); // Persist updates in localStorage
    };

    const updateColumnOrder = (newOrder: string[]) => {
        const firstColumnKey = SELECT_COLUMN_ID;
        const lastColumnKey = ACTIONS_COLUMN_ID;

        const filteredOrder = newOrder.filter(
            key => key !== firstColumnKey && key !== lastColumnKey,
        );

        setColumnOrder(newOrder);
        setLocallyStoredColumnOrder(filteredOrder);
    };

    const handleChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
    };

    const handleDealGroupChange = (option: { label: string; value: number | null } | null) => {
        setSelectedDealGroup(option?.value || undefined);
    };

    const dealGroupOptions = dealGroups
        ? [{ label: 'All deals', value: undefined }, ...dealGroups.map(group => ({ label: group.name, value: group.id }))]
        : [];

    const createOptions = [
        {
            label: 'Email deal',
            value: 'email',
            className: 'heap-deal-pipeline-email-deal',
            tooltip: 'Email a PDF document that corresponds to your new deal to Northspyre',
        },
        { label: 'Create deal', value: 'create', className: 'heap-deal-pipeline-create-deal' },
        { label: 'Bulk upload deals', value: 'bulkUpload', className: 'heap-deal-pipeline-bulk-upload-deals' },
    ];

    useEffect(() => setCurrentPage(1), [debouncedValue]);

    const toggleDeleteDealGroupModal = () => setIsOpenDeleteDealGroupModal(!isOpenDeleteDealGroupModal);
    const toggleRenameDealGroupModal = () => setIsOpenRenameDealGroupModal(!isOpenRenameDealGroupModal);
    const dealGroupName = dealGroupOptions.find(option => option.value === selectedDealGroup) || null;

    const onDeleteDealGroupSuccess = () => {
        setSelectedDealGroup(undefined);
    };

    const emptyMessage = selectedDealGroup ? 'No deals in this group.' : 'No deals within phase(s).';

    const { data: integrationStatus } = useQuery(getCRMIntegrationStatusQueryOptions(selectedOrganizationId!));
    const hasNotIntegratedEmail = !!integrationStatus && !(integrationStatus.google || integrationStatus.microsoft);

    const handleDismissEmailIntegrationBanner = () => {
        setIsOpenEmailIntegrationModal(false);
        setShowBanners({
            ...showBanners,
            showEmailIntegration: false,
        });
    };

    const handleCreateProFormaClick = () => {
        if (dealsWithProFormas && dealsWithProFormas.length === 1) {
            setSelectedDealIdForProForma(dealsWithProFormas[0].id);
            setOpenCreateProFormaModal(true);
        } else {
            setShowDealSelectionModal(true);
        }
    };

    const handleDealSelectionForProForma = () => {
        if (!selectedDealOption) return;

        const dealId = selectedDealOption.value as number;
        setShowDealSelectionModal(false);
        setSelectedDealIdForProForma(dealId);
        setSelectedDealOption(null);
        setOpenCreateProFormaModal(true);
    };

    const handleModalClose = () => {
        setShowDealSelectionModal(false);
        setSelectedDealOption(null);
    };

    const toggleCreateProFormaModal = () => {
        setOpenCreateProFormaModal(!openCreateProFormaModal);
        if (openCreateProFormaModal) {
            setSelectedDealIdForProForma(null);
        }
    };

    const [bannersKey, setBannersKey] = useState<number>(() => Date.now()); // tracks when banners are updated, to force re-render
    const banners = useMemo(() => {
        const bannersList: INSInfoBannerProps[] = [];

        if (signUpStage && signUpStage === SignUpStage.ERROR) {
            bannersList.push({
                title: 'Something went wrong while creating your deal.',
                subtitle: (
                    <span>
                        It looks like your data didn't carry over after sign-up. You'll need to create a new deal.
                    </span>
                ),
                className: 'mt-2 NSCard--warning',
                showIcon: true,
                warning: true,
                customInfo: (
                    <div>
                        <NSButton
                            className="mr-2 text-white"
                            color="dark-lighten"
                            id="dismiss-create-combined-report-btn"
                            callback={() => {
                                clearLocalStorage(PageSpecificKeys.SignUpStage);
                                setBannersKey(Date.now());
                            }}
                        >
                            Dismiss
                        </NSButton>
                        <NSButton
                            className="NSButton--inner-content-bold"
                            color="primary"
                            id="create-combined-report-btn"
                            callback={() => {
                                navigate(generatePath(URLS.DEALS.FORM, { dealId: 'new' }));
                            }}
                        >
                            Create new deal
                        </NSButton>
                    </div>
                ),
            });
        }

        if (showCreateCombinedReport && (showBanners.showCreateCombinedReport === undefined || showBanners.showCreateCombinedReport)) {
            bannersList.push({
                title: 'Want the full picture?',
                subtitle: (
                    <span>
                        You have multiple deals with complete pro formas -
                        {' '}
                        <Link
                            to={URLS.REPORTS.NEW.replace(':reportType', ReportTypeToUrlParamMap[ReportType.Aggregate])}
                            className="text-primary"
                        >
                            create a new Combined Deal report
                        </Link>
                        {' '}
                        to view total performance across deals.
                    </span>
                ),
                className: 'mt-2 NSCard__left-border--info',
                showIcon: false,
                customInfo: (
                    <div>
                        <NSButton
                            className="mr-2 text-white"
                            color="dark-lighten"
                            id="dismiss-create-combined-report-btn"
                            callback={() => {
                                setShowBanners({
                                    ...showBanners,
                                    showCreateCombinedReport: false,
                                });
                            }}
                        >
                            Dismiss
                        </NSButton>
                        <NSButton
                            className="NSButton--inner-content-bold"
                            color="primary"
                            id="create-combined-report-btn"
                            callback={() => {
                                navigate(generatePath(URLS.REPORTS.NEW, { reportType: ReportTypeToUrlParamMap[ReportType.Aggregate] }));
                            }}
                        >
                            Create new report
                        </NSButton>
                    </div>
                ),
            });
        }

        if (hasNotIntegratedEmail && (showBanners.showEmailIntegration === undefined || showBanners.showEmailIntegration)) {
            bannersList.push({
                highlightTitle: 'Finish account setup',
                title: 'Integrate your email account with Northspyre Deal',
                subtitle: (
                    <span>
                        Connect your Google or Outlook email to automatically log deal-related communications using associated contacts.
                    </span>
                ),
                className: 'mt-2 NSCard__left-border--info',
                showIcon: false,
                customInfo: (
                    <div>
                        <NSButton
                            className="mr-2 text-white"
                            color="secondary"
                            id="dismiss-email-integration-banner-btn"
                            callback={() => setIsOpenEmailIntegrationModal(true)}
                        >
                            Dismiss
                        </NSButton>
                        <Link
                            to={URLS.PROJECTS_APP.USER_SETTINGS}
                            target="_blank"
                            rel="noreferrer"
                            className="cursor--pointer"
                        >
                            <NSButton
                                id="integrate-email-btn"
                                text="Set up"
                                color="primary"
                                className="heap-deal-pipeline-email-setup"
                                outline={false}
                            />
                        </Link>
                    </div>
                ),
            });
        }

        return bannersList;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showBanners, showCreateCombinedReport, bannersKey]);

    const handleSetSorting = useCallback<React.Dispatch<React.SetStateAction<SortingState>>>(
        updaterOrValue => {
            setSorting(prevSorting => {
                const nextSorting = typeof updaterOrValue === 'function'
                    ? updaterOrValue(prevSorting)
                    : updaterOrValue;
                setStoredSorting(nextSorting);
                return nextSorting;
            });
        },
        [setStoredSorting],
    );

    return (
        <DealsOverviewProvider
            selectedStages={derivedSelectedStages}
            setSelectedStages={setSelectedStages}
            isExpandedPipeline={isExpandedPipeline}
            setIsExpandedPipeline={setIsExpandedPipeline}
        >
            <PipelineSidebar isExpandedPipeline={isExpandedPipeline} setIsExpandedPipeline={setIsExpandedPipeline} />
            <div
                className={classNames('Deals', {
                    'Deals--empty': isEmpty,
                    'Deals--padding-collapsed': !isExpandedPipeline,
                    'Deals--padding-expanded': isExpandedPipeline,
                })}
            >
                <div className="Deals__header-container d-flex justify-content-between align-items-center">
                    <div>
                        <div className="d-flex align-items-center">
                            <p className="Deals__header-container__title">Deals</p>
                            <NSBadge
                                className="ml-1"
                                color="primary-lighten"
                            >
                                Beta
                            </NSBadge>
                        </div>
                        <p className="Deals__header-container__subtitle">
                            {deals?.itemCount}
                            {' '}
                            active deals
                        </p>
                    </div>

                    <div className="d-flex align-items-center">
                        {selectedDealGroup !== undefined ? (
                            <DealGroupMenu onRename={toggleRenameDealGroupModal} onDelete={toggleDeleteDealGroupModal} />
                        ) : null}
                        <NSSelect
                            formClassName="Deals__select"
                            options={dealGroupOptions}
                            value={dealGroupName}
                            onChange={handleDealGroupChange}
                            name="DealGroup"
                            menuPosition="absolute"
                            isClearable={false}
                            isLoading={isLoadingDealGroups}
                            tabIndex={0}
                        />
                        <div className="Deals__vertical-divider" />
                        <Link to={URLS.REPORTS.HOME} className="mr-2 heap-deal-pipeline-reports">
                            <NSButton
                                innerContentClassName="NSButton--inner-content-bold"
                                color="secondary"
                                outline={false}
                                id="reports-btn"
                                allowReadOnlyAccess
                            >
                                Reports
                            </NSButton>
                        </Link>
                        <NSButton
                            innerContentClassName="NSButton--inner-content-bold"
                            color="primary"
                            outline={false}
                            id="create-pro-forma-btn"
                            className="mr-2 heap-deal-pipeline-create-pro-forma"
                            callback={handleCreateProFormaClick}
                            disabled={!dealsWithProFormas || dealsWithProFormas.length === 0}
                            disabledTooltip={DISABLED_PRO_FORMA_BTN_TOOLTIP}
                        >
                            Create pro forma
                        </NSButton>
                        <NSDropdown
                            options={createOptions}
                            selectedOption={createOptions[1]}
                            handleSelectOption={selectedOption => {
                                if (selectedOption?.value === 'create') {
                                    navigate(generatePath(URLS.DEALS.FORM, { dealId: 'new' }));
                                } else if (selectedOption?.value === 'bulkUpload') {
                                    navigate(generatePath(URLS.DEALS.BULK_UPLOAD));
                                } else if (selectedOption?.value === 'email') {
                                    window.location.href = `${URLS.PROJECTS_APP.APPROVALS}?emailDeal=true`;
                                }
                            }}
                            toggleClassName="bg-primary font-weight-bold rounded"
                            hideOptionCheckmarkIcon
                        />
                    </div>
                </div>
                {banners && banners.length > 0 && <BannerCarousel bannersData={banners} />}
                <div className="d-flex justify-content-between mt-2">
                    <NavMenu activeSection={activeSection} setActiveSection={setActiveSection} />
                    <div className="d-flex w-25">
                        <VisibleColumnsDropdown
                            visibleColumns={visibleColumns}
                            updateVisibleColumns={updateVisibleColumns}
                            columnOptions={COLUMN_OPTIONS}
                            isOptionDisplayed={isOptionDisplayed}
                        />
                        <NSInput
                            type="search"
                            name="search-bar"
                            placeholder="Search"
                            value={searchValue}
                            onChange={handleChangeSearchValue}
                            customClassName="bg-secondary border-0 px-0 w-100"
                            inputGroupClassName="bg-secondary border-0"
                            prependInputAddon={<FontAwesomeIcon className="text-muted" icon={faMagnifyingGlass} />}
                        />
                    </div>
                </div>
                <RenderIf isTrue={!noDealsAcrossAllStages && selectedSubprojectStageIds !== '' && deals?.items?.length === 0}>
                    <div className="d-flex text-center text-white align-items-center justify-content-center Deals__no-deals-message">
                        {emptyMessage}
                    </div>
                </RenderIf>
                {
                    noDealsAcrossAllStages && <EmptyState />
                }
                <RenderIf
                    isTrue={activeSection === DEALS_SECTIONS.MAP && deals?.items && deals?.items?.length > 0}
                    isLoading={dealsAreLoading && activeSection === DEALS_SECTIONS.MAP}
                >
                    <MapView selectedDeal={selectedDeal} />
                    <DealsTable
                        deals={deals?.items || []}
                        visibleColumns={visibleColumns}
                        selectedDeal={selectedDeal}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        totalItems={deals?.itemCount || 0}
                        handleSelectDeal={handleDealSelection}
                        onAscendingSort={handleAscendingSort}
                        onDescendingSort={handleDescendingSort}
                        columnOrder={columnOrder}
                        onColumnOrderChange={updateColumnOrder}
                        sorting={sorting}
                        setSorting={handleSetSorting}
                        selectedUnitType={selectedUnitType}
                        handleSelectUnitType={handleSelectUnitType}
                    />
                </RenderIf>
                <RenderIf
                    isTrue={activeSection === DEALS_SECTIONS.LIST && deals?.items && deals?.items?.length > 0}
                    isLoading={dealsAreLoading && activeSection === DEALS_SECTIONS.LIST}
                >
                    <DealsTable
                        className="pt-2"
                        deals={deals?.items || []}
                        visibleColumns={visibleColumns}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        totalItems={deals?.itemCount || 0}
                        handleSelectDeal={handleDealSelection}
                        onAscendingSort={handleAscendingSort}
                        onDescendingSort={handleDescendingSort}
                        columnOrder={columnOrder}
                        onColumnOrderChange={updateColumnOrder}
                        sorting={sorting}
                        setSorting={handleSetSorting}
                        selectedUnitType={selectedUnitType}
                        handleSelectUnitType={handleSelectUnitType}
                    />
                </RenderIf>
                <RenameDealGroupModal
                    toggle={toggleRenameDealGroupModal}
                    isOpen={isOpenRenameDealGroupModal}
                    orgId={selectedOrganizationId!}
                    dealGroupId={selectedDealGroup}
                    currentGroupName={dealGroupName !== null ? dealGroupName.label : null}
                />
                <DeleteDealGroupModal
                    toggle={toggleDeleteDealGroupModal}
                    isOpen={isOpenDeleteDealGroupModal}
                    dealGroupId={selectedDealGroup}
                    dealName={dealGroupName !== null ? dealGroupName.label : null}
                    onDeleteSuccess={onDeleteDealGroupSuccess}
                />
                <NSModal
                    isOpen={isOpenEmailIntegrationModal}
                    toggle={() => setIsOpenEmailIntegrationModal(false)}
                    modalHeaderClassName="bg-primary text-white font-weight-bold"
                    modalHeaderText="Unlock more with email integration"
                    size="md"
                    customFooter={(
                        <>
                            <NSButton
                                className="mr-2 text-white"
                                color="secondary"
                                id="dismiss-email-integration-banner-btn"
                                callback={handleDismissEmailIntegrationBanner}
                            >
                                I'll do this later
                            </NSButton>
                            <Link
                                to={URLS.PROJECTS_APP.USER_SETTINGS}
                                target="_blank"
                                rel="noreferrer"
                                className="cursor--pointer"
                            >
                                <NSButton
                                    id="integrate-email-btn"
                                    text="Integrate email"
                                    color="primary"
                                    outline={false}
                                />
                            </Link>
                        </>
                    )}
                >
                    <div className="mb-0 text-dark font-weight-bold font-14">Connecting your email powers the best of Northspyre Deal</div>
                    <div className="text-muted font-14">
                        Auto-track deal activity, centralize communications, and surface key insights.
                        You can always integrate your email later in
                        <span className="text-white font-weight-bold"> Settings &gt; General</span>
                        .
                    </div>
                </NSModal>

                <NSModal
                    isOpen={showDealSelectionModal}
                    toggle={handleModalClose}
                    modalHeaderText="Select a deal to create pro forma for"
                    modalHeaderClassName="text-white bg-primary"
                    modalFooterButtonText="Continue"
                    modalFooterButtonColor="primary"
                    modalFooterCancelButtonText="Cancel"
                    modalFooterButtonFunction={handleDealSelectionForProForma}
                    modalFooterIsButtonFunctionDisabled={!selectedDealOption}
                >
                    <NSSelect
                        name="deal-selection"
                        label="Select Deal"
                        placeholder="Search for a deal..."
                        options={dealsWithProFormas?.map(deal => ({
                            label: deal.dealName,
                            value: deal.id,
                        })) || []}
                        value={selectedDealOption}
                        onChange={(option: IOptionAnyValue) => setSelectedDealOption(option)}
                        isClearable
                    />
                </NSModal>

                <RenderIf isTrue={openCreateProFormaModal && selectedDealIdForProForma !== null}>
                    <CreateProFormaModal
                        toggle={toggleCreateProFormaModal}
                        isOpen={openCreateProFormaModal}
                        dealId={selectedDealIdForProForma || undefined}
                        dealAssetTypeCorrelationId={
                            dealsWithProFormas?.find(d => d.id === selectedDealIdForProForma)?.assetTypeCorrelationId || undefined
                        }
                    />
                </RenderIf>
            </div>
        </DealsOverviewProvider>
    );
};

export default Deals;
